import { formatTimestamp } from "../../../utils/helper";

const FIELD_LABELS: any = {
    isServiceable: "Serviceable",
    isCod: "Payment",
    zipcode: "Zipcode",
    categoryCode: "Category",
    cityId: "City",
    countryCode: "Country",
    msn: "MSN",
}

// Fields worth printing when we show a whole record (create / delete) — identity
// fields like id/msn are skipped, the row already carries them.
const SNAPSHOT_FIELDS = ["isServiceable", "isCod", "zipcode", "categoryCode", "cityId", "countryCode"]

// Their pills read as words ("COD", "Not Serviceable"), so a "Payment:" / "Serviceable:"
// prefix would just say it twice.
const SELF_DESCRIBING_FIELDS = ["isCod", "isServiceable"]

const KIND_STYLES: any = {
    created: "bg-green-100 text-green-700",
    updated: "bg-amber-100 text-amber-700",
    deleted: "bg-red-100 text-red-700",
}

const KIND_LABELS: any = {
    created: "Created",
    updated: "Updated",
    deleted: "Deleted",
}

export const fieldLabel = (field: string) => FIELD_LABELS[field] || field

export const actionLabel = (action: string) =>
    (action || "").split("_").map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(" ")

/**
 * The raw `action` does not tell you what happened — BULK_CREATE is really an
 * upsert and shows up as both a create and a field update. Derive the meaning
 * from which side of the diff is populated, and keep the raw action as detail.
 */
export const auditKind = (log: any) => {
    const hasOld = log?.oldValue !== null && log?.oldValue !== undefined
    const hasNew = log?.newValue !== null && log?.newValue !== undefined
    if (hasOld && hasNew) return "updated"
    if (hasNew) return "created"
    if (hasOld) return "deleted"
    return (log?.action || "").includes("DELETE") ? "deleted" : "updated"
}

/** Fields that genuinely differ between old and new — the API sometimes lists unchanged ones. */
export const changedDiffFields = (log: any) => {
    const declared = (log?.changedFields || []).filter((f: string) => f !== "id")
    const candidates = declared.length
        ? declared
        : Object.keys({ ...(log?.oldValue || {}), ...(log?.newValue || {}) }).filter((f) => f !== "id")
    return candidates.filter((f: string) => log?.oldValue?.[f] !== log?.newValue?.[f])
}

/** One-line summary used by the collapsed row. */
export const summarizeLog = (log: any) => {
    const kind = auditKind(log)
    if (kind !== "updated") return KIND_LABELS[kind]
    const fields = changedDiffFields(log).map(fieldLabel)
    if (!fields.length) return "Updated"
    if (fields.length <= 2) return fields.join(", ")
    return `${fields.slice(0, 2).join(", ")} +${fields.length - 2}`
}

export const timeAgo = (timestamp: any) => {
    if (!timestamp) return ""
    const diff = Date.now() - Number(timestamp)
    if (diff < 0) return "just now"
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return "just now"
    if (mins < 60) return `${mins}m ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months}mo ago`
    return `${Math.floor(months / 12)}y ago`
}

/** `isCod` is not a yes/no flag to the business — it is the payment mode. */
export const PaymentModePill = (props: { value: any, className?: string }) => {
    const { value, className = "" } = props
    const isCod = value === true || value === "true"
    return (
        <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded text-[11px] font-medium border ${isCod ? "bg-indigo-100 text-indigo-800 border-indigo-200" : "bg-teal-100 text-teal-800 border-teal-200"} ${className}`}>
            {isCod ? "COD" : "Prepaid"}
        </span>
    )
}

export const ServiceablePill = (props: { value: any, className?: string }) => {
    const { value, className = "" } = props
    const isServiceable = value === true || value === "true"
    return (
        <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded text-[11px] font-medium border ${isServiceable ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"} ${className}`}>
            {isServiceable ? "Serviceable" : "Not Serviceable"}
        </span>
    )
}

const ValuePill = (props: { value: any, field?: string }) => {
    const { value, field } = props
    if (value === null || value === undefined || value === "") {
        return <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[11px]">—</span>
    }
    if (field === "isCod") {
        return <PaymentModePill value={value} />
    }
    if (field === "isServiceable") {
        return <ServiceablePill value={value} />
    }
    if (typeof value === "boolean") {
        return (
            <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${value ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                {value ? "Yes" : "No"}
            </span>
        )
    }
    return <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px]">{String(value)}</span>
}

/** Flat value list for a whole record — used when there is no before/after to diff. */
const Snapshot = (props: { record: any }) => {
    const { record } = props
    const fields = SNAPSHOT_FIELDS.filter((f) => record?.[f] !== null && record?.[f] !== undefined)
    if (!fields.length) return null
    return (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
            {fields.map((field) => (
                <span key={field} className="flex items-center gap-1 text-[11px] text-[#3F3F3F]">
                    {SELF_DESCRIBING_FIELDS.includes(field) ? null : <span className="text-gray-500">{fieldLabel(field)}:</span>}
                    <ValuePill value={record[field]} field={field} />
                </span>
            ))}
        </div>
    )
}

const ServiceabilityAuditLog = (props: any) => {
    const { logs } = props

    const sortedLogs = [...(logs || [])].sort((a: any, b: any) => (b?.createdAt || 0) - (a?.createdAt || 0))

    if (!sortedLogs.length) {
        return <p className="text-xs text-gray-500 py-2">No change history available.</p>
    }

    return (
        <div className="py-2">
            <p className="text-[11px] font-semibold text-[#43425D] uppercase tracking-wide mb-2">
                Change history ({sortedLogs.length})
            </p>
            <ul className="border-l-2 border-gray-200 ml-1.5">
                {sortedLogs.map((log: any, index: number) => {
                    const kind = auditKind(log)
                    const diffFields = kind === "updated" ? changedDiffFields(log) : []

                    return (
                        <li key={log?.id || index} className="relative pl-4 pb-3 last:pb-0">
                            <span className={`absolute -left-[5px] top-1.5 w-2 h-2 rounded-full ${kind === "deleted" ? "bg-red-500" : kind === "created" ? "bg-green-600" : "bg-amber-500"}`} />

                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${KIND_STYLES[kind]}`}>
                                    {KIND_LABELS[kind]}
                                </span>
                                <span className="text-xs text-[#3F3F3F] font-medium">{log?.performedBy || "System"}</span>
                                <span className="text-[11px] text-gray-500" title={formatTimestamp(log?.createdAt)}>
                                    {timeAgo(log?.createdAt)} · {formatTimestamp(log?.createdAt)}
                                </span>
                                <span className="text-[10px] text-gray-400" title="Operation performed on the backend">
                                    via {actionLabel(log?.action)}
                                </span>
                                {log?.affectedCount ? (
                                    <span className="text-[11px] text-gray-500">· {log.affectedCount} records affected</span>
                                ) : null}
                            </div>

                            <div className="mt-1">
                                {kind === "updated" ? (
                                    diffFields.length ? (
                                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                                            {diffFields.map((field: string) => (
                                                <span key={field} className="flex items-center gap-1 text-[11px] text-[#3F3F3F]">
                                                    {SELF_DESCRIBING_FIELDS.includes(field) ? null : <span className="text-gray-500">{fieldLabel(field)}:</span>}
                                                    <ValuePill value={log?.oldValue?.[field]} field={field} />
                                                    <span className="text-gray-400">→</span>
                                                    <ValuePill value={log?.newValue?.[field]} field={field} />
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-[11px] text-gray-500">Re-submitted with no field changes.</p>
                                    )
                                ) : (
                                    <>
                                        <p className="text-[11px] text-gray-500 mb-1">
                                            {kind === "created" ? "Record added with" : "Record removed, last known values"}
                                            {kind === "deleted" && log?.recordId ? ` (#${log.recordId})` : ""}:
                                        </p>
                                        <Snapshot record={kind === "created" ? log?.newValue : log?.oldValue} />
                                    </>
                                )}
                            </div>

                            {log?.criteria ? (
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Criteria: {typeof log.criteria === "string" ? log.criteria : JSON.stringify(log.criteria)}
                                </p>
                            ) : null}

                            {log?.traceId ? (
                                <p className="text-[10px] text-gray-400 mt-1">Trace: {log.traceId}</p>
                            ) : null}
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export default ServiceabilityAuditLog;
